import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { BrowserRouter, Link, Route, Routes, useLocation, useParams } from 'react-router-dom';
import type { Provider, User } from '@supabase/supabase-js';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Clipboard,
  Download,
  ExternalLink,
  Lock,
  LogOut,
  ShieldCheck,
  Sparkles,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { childSafetySections, privacySections, termsSections } from './policies';
import { supabase } from './supabase';
import './styles.css';

declare global {
  interface Window {
    __AGIT_INVITE_CODE__?: string;
    __AGIT_ROOT__?: Root;
  }
}

const asset = (path: string) => path;
const appStoreUrl = 'https://apps.apple.com/kr/app/agit/id6776614044';
const googlePlayUrl = 'https://play.google.com/store/apps/details?id=com.company.agit&hl=ko';
const CanvasExperience = React.lazy(() => import('./CanvasExperience'));

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/invite/:code" element={<InvitePage />} />
        <Route path="/privacy" element={<PolicyPage kind="privacy" />} />
        <Route path="/terms" element={<PolicyPage kind="terms" />} />
        <Route path="/child-safety" element={<PolicyPage kind="childSafety" />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <header className="site-header">
        <Link className="brand-link" to="/" aria-label="Agit 홈">
          <img src="/assets/brand/agit_icon-cutout.png" alt="" />
          <span>Agit</span>
        </Link>
        <nav aria-label="주요 메뉴">
          <Link to="/privacy">개인정보처리방침</Link>
          <Link to="/terms">이용약관</Link>
          <Link to="/child-safety">아동 안전</Link>
          <Link to="/delete-account">계정 삭제</Link>
        </nav>
      </header>
      {children}
    </>
  );
}

function HomePage() {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const nextProgress = scrollable > 0 ? window.scrollY / scrollable : 0;
      setProgress(Math.min(1, Math.max(0, nextProgress)));
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Shell>
      <main>
        <div className="scroll-meter" aria-hidden="true">
          <span style={{ transform: `scaleX(${progress})` }} />
        </div>

        <section className="hero-section">
          <div className="hero-copy">
            <h1>
              <span>친구, 연인과 함께 꾸미는</span>
              <span>우리만의 공유 위젯</span>
            </h1>
            <img className="hero-logo hero-screenshot" src="/assets/onboarding/shared-widget-phones-tight.png" alt="" />
            <StoreBadges />
          </div>
        </section>

        <React.Suspense
          fallback={
            <section className="canvas-experience canvas-experience-loading" aria-label="캔버스 체험 불러오는 중" />
          }
        >
          <CanvasExperience />
        </React.Suspense>

        <section className="feature-band">
          <div>
            <Sparkles aria-hidden="true" />
            <h2>사진, 스티커, 손그림으로</h2>
            <p>작은 캔버스 위에 오늘의 기분을 올리고 우리만의 위젯을 만들어요.</p>
          </div>
          <div>
            <Smartphone aria-hidden="true" />
            <h2>친구 화면에도 그대로</h2>
            <p>공유한 순간 같은 Agit에 있는 사람들의 홈 화면 위젯에 같은 장면이 보여요.</p>
          </div>
          <div>
            <Lock aria-hidden="true" />
            <h2>매일 가볍게 바꾸기</h2>
            <p>말로 보내기 애매한 마음도 낙서와 이미지로 잠금화면 가까이에 남겨요.</p>
          </div>
        </section>
      </main>
      <Footer />
    </Shell>
  );
}

function InvitePage() {
  const params = useParams();
  const location = useLocation();
  const [copied, setCopied] = React.useState(false);
  const rawCode =
    params.code ||
    new URLSearchParams(location.search).get('code') ||
    window.__AGIT_INVITE_CODE__ ||
    '';
  const inviteCode = sanitizeInviteCode(rawCode);
  const schemeUrl = inviteCode ? `com.company.agit://invite/${encodeURIComponent(inviteCode)}` : 'com.company.agit://home';

  const copyCode = async () => {
    if (!inviteCode) return;
    await copyToClipboard(inviteCode);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <Shell>
      <main className="invite-page">
        <section className="invite-card">
          <img src="/assets/brand/agit_header_logo-cutout.png" alt="Agit" />
          <p className="eyebrow">Agit 초대장</p>
          <h1>{inviteCode ? '같은 위젯을 함께 꾸며요.' : '초대코드가 필요해요.'}</h1>
          <p className="invite-description">
            {inviteCode
              ? '앱이 설치되어 있다면 아래 버튼으로 Agit을 열고, 설치 전이라면 초대코드를 복사해 두세요.'
              : '공유받은 초대 링크를 다시 확인하거나 앱에서 초대코드를 직접 입력해 주세요.'}
          </p>

          <div className="invite-code" aria-label="초대코드">
            {inviteCode || 'NO CODE'}
          </div>

          <div className="invite-actions">
            <a className="primary-button" href={schemeUrl}>
              앱에서 열기
              <ExternalLink aria-hidden="true" size={18} />
            </a>
            <button className="secondary-button" type="button" onClick={copyCode} disabled={!inviteCode}>
              {copied ? <Check aria-hidden="true" size={18} /> : <Clipboard aria-hidden="true" size={18} />}
              {copied ? '복사됨' : '코드 복사'}
            </button>
          </div>

          <div className="download-panel" aria-label="앱 다운로드">
            <p>앱 다운로드</p>
            <div>
              <a
                href={appStoreUrl}
                target="_blank"
                rel="noreferrer"
              >
                <Download aria-hidden="true" size={18} />
                App Store에서 다운로드
              </a>
              <a
                href={googlePlayUrl}
                target="_blank"
                rel="noreferrer"
              >
                <Download aria-hidden="true" size={18} />
                Google Play에서 다운로드
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </Shell>
  );
}

function StoreBadges() {
  return (
    <div className="store-badges" aria-label="앱 다운로드">
      <a href={googlePlayUrl} target="_blank" rel="noreferrer" aria-label="Google Play에서 Agit 다운로드">
        <img src="/assets/store/google-play-badge-cropped.png" alt="Get it on Google Play" />
      </a>
      <a href={appStoreUrl} target="_blank" rel="noreferrer" aria-label="App Store에서 Agit 다운로드">
        <img src="/assets/store/download-on-app-store.svg" alt="App Store에서 다운로드" />
      </a>
    </div>
  );
}

const authProviders: Array<{ provider: Provider; label: string }> = [
  { provider: 'apple', label: 'Apple로 본인 확인' },
  { provider: 'google', label: 'Google로 본인 확인' },
  { provider: 'kakao', label: 'Kakao로 본인 확인' },
];

function DeleteAccountPage() {
  const location = useLocation();
  const [user, setUser] = React.useState<User | null>(null);
  const [loadingSession, setLoadingSession] = React.useState(true);
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState('');
  const [completed, setCompleted] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState('');
  const canDelete = confirmText.trim() === '계정 삭제' && user != null && !working;

  React.useEffect(() => {
    let mounted = true;
    const params = new URLSearchParams(location.search);
    const authError = params.get('error_description') || params.get('error');
    if (authError) setError(authError);

    supabase.auth.getSession().then(({ data, error: sessionError }) => {
      if (!mounted) return;
      if (sessionError) setError(sessionError.message);
      setUser(data.session?.user ?? null);
      setLoadingSession(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoadingSession(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [location.search]);

  const signIn = async (provider: Provider) => {
    setError('');
    const { error: signInError } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/delete-account`,
      },
    });
    if (signInError) setError(signInError.message);
  };

  const signOut = async () => {
    setError('');
    await supabase.auth.signOut();
    setUser(null);
    setConfirmText('');
  };

  const deleteAccount = async () => {
    if (!canDelete) return;
    setWorking(true);
    setError('');
    try {
      const { error: deleteError } = await supabase.functions.invoke('delete-account');
      if (deleteError) throw deleteError;
      await supabase.auth.signOut();
      setUser(null);
      setCompleted(true);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : '계정 삭제에 실패했습니다.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <Shell>
      <main className="account-delete-page">
        <section className="account-delete-card">
          <div className="delete-icon" aria-hidden="true">
            {completed ? <ShieldCheck size={30} /> : <Trash2 size={30} />}
          </div>
          <p className="eyebrow">Agit 계정 삭제</p>
          <h1>{completed ? '계정 삭제 요청이 완료됐어요.' : '앱 없이도 계정을 삭제할 수 있어요.'}</h1>

          {completed ? (
            <div className="delete-complete">
              <p>
                계정, 프로필, 기기 토큰과 개인 저장 파일 삭제를 요청했습니다. 그룹에 이미 공유된
                콘텐츠는 식별정보가 제거된 상태로 유지될 수 있습니다.
              </p>
              <Link className="secondary-button" to="/">
                홈으로 이동
                <ArrowRight aria-hidden="true" size={18} />
              </Link>
            </div>
          ) : (
            <>
              <p className="delete-description">
                가입에 사용한 소셜 계정으로 본인 확인을 완료한 뒤 계정을 삭제합니다. 삭제 후 같은
                계정의 프로필, 기기 토큰, 위젯 설정과 비공유 저장 파일은 복구할 수 없습니다.
              </p>

              {loadingSession ? (
                <div className="delete-status">로그인 상태를 확인하고 있어요.</div>
              ) : user ? (
                <div className="delete-confirm-panel">
                  <div className="signed-in-box">
                    <ShieldCheck aria-hidden="true" size={22} />
                    <div>
                      <strong>본인 확인 완료</strong>
                      <span>{user.email ?? user.id}</span>
                    </div>
                    <button type="button" onClick={signOut} disabled={working} aria-label="로그아웃">
                      <LogOut aria-hidden="true" size={18} />
                    </button>
                  </div>

                  <div className="delete-warning">
                    <AlertTriangle aria-hidden="true" size={20} />
                    <p>
                      삭제를 진행하려면 아래 입력란에 <strong>계정 삭제</strong>를 입력하세요.
                    </p>
                  </div>

                  <label className="delete-input-label" htmlFor="delete-confirm-text">
                    확인 문구
                  </label>
                  <input
                    id="delete-confirm-text"
                    value={confirmText}
                    onChange={(event) => setConfirmText(event.target.value)}
                    placeholder="계정 삭제"
                    disabled={working}
                  />

                  <button className="danger-button" type="button" onClick={deleteAccount} disabled={!canDelete}>
                    <Trash2 aria-hidden="true" size={18} />
                    {working ? '삭제 중' : '계정 삭제'}
                  </button>
                </div>
              ) : (
                <div className="delete-login-panel">
                  {authProviders.map((item) => (
                    <button key={item.provider} type="button" onClick={() => signIn(item.provider)}>
                      {item.label}
                      <ExternalLink aria-hidden="true" size={18} />
                    </button>
                  ))}
                </div>
              )}

              {error ? <p className="delete-error">{error}</p> : null}
            </>
          )}
        </section>
      </main>
      <Footer />
    </Shell>
  );
}

type PolicyKind = 'privacy' | 'terms' | 'childSafety';

function PolicyPage({ kind }: { kind: PolicyKind }) {
  const isPrivacy = kind === 'privacy';
  const isTerms = kind === 'terms';
  const title = isPrivacy ? '개인정보처리방침' : isTerms ? '서비스 이용약관' : 'Agit 아동 안전 표준';
  const sections = isPrivacy ? privacySections : isTerms ? termsSections : childSafetySections;
  const effectiveDate = kind === 'childSafety' ? '2026년 6월 14일' : '2026년 6월 19일';
  const version = kind === 'childSafety' ? '1.0' : '1.2';
  const previousVersionText =
    kind === 'childSafety'
      ? '최초 시행 문서로 이전 버전이 없습니다.'
      : '버전 1.1: 2026년 6월 19일 구독 처리 고지 보강 / 버전 1.0: 2026년 6월 13일 최초 시행';
  const intro = isPrivacy
    ? '할리는 Agit 이용자의 개인정보를 중요하게 생각하며, 개인정보 보호법 등 관련 법령을 준수합니다.'
    : isTerms
      ? '이 약관은 Agit을 안전하고 즐겁게 이용하기 위해 회사와 이용자가 지켜야 할 기준을 정합니다.'
      : '할리는 Agit에서 아동 성적 학대 및 착취와 아동 성적 학대 자료를 명확히 금지하고 신속한 신고·조치 절차를 운영합니다.';

  return (
    <Shell>
      <main className="document-page">
        <article>
          <p className="eyebrow">Agit legal</p>
          <h1>{title}</h1>
          <div className="document-meta">
            <span>시행일 {effectiveDate}</span>
            <span>버전 {version}</span>
          </div>
          <p className="document-intro">{intro}</p>
          {sections.map((section) => (
            <section key={section.id} id={section.id}>
              <h2>{section.title}</h2>
              {section.content}
            </section>
          ))}
          <section id="history" className="policy-history">
            <h2>이전 버전</h2>
            <p>{previousVersionText}</p>
          </section>
        </article>
      </main>
      <Footer />
    </Shell>
  );
}

function NotFoundPage() {
  return (
    <Shell>
      <main className="not-found">
        <h1>페이지를 찾을 수 없어요.</h1>
        <Link className="primary-button" to="/">
          홈으로 이동
          <ArrowRight aria-hidden="true" size={18} />
        </Link>
      </main>
      <Footer />
    </Shell>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <span>Agit</span>
      <nav aria-label="하단 메뉴">
        <Link to="/privacy">개인정보처리방침</Link>
        <Link to="/terms">서비스 이용약관</Link>
        <Link to="/child-safety">아동 안전</Link>
        <Link to="/delete-account">계정 삭제</Link>
      </nav>
    </footer>
  );
}

function sanitizeInviteCode(value: string) {
  return value.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64).toUpperCase();
}

async function copyToClipboard(value: string) {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(value);
      return;
    }
  } catch {
    // Fall through to the textarea fallback below.
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

const rootElement = document.getElementById('root')!;
const root = window.__AGIT_ROOT__ ?? createRoot(rootElement);
window.__AGIT_ROOT__ = root;

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
